import { EVENTS_ENABLED } from '../../constants/featureFlags';

/** Who may open an item: everyone, only admins (and owners), or only owners. */
export type AccountNavAccess = 'any' | 'admin' | 'owner';

export interface AccountNavItem {
  id: string;
  icon: string;
  label: string;
  path: string;
  access?: AccountNavAccess;
  comingSoon?: boolean;
}

export interface AccountNavSection {
  title: string;
  items: AccountNavItem[];
}

// Kalligo's workspace-level shell groups its nav under OVERVIEW, PIPELINE and
// DELIVERY, with the workspace's own admin (Team, Settings) at the foot; Ans
// keeps those groups and its own wording (Client Quotes, Vendor Directory).
// "At a glance" is narrower than Kalligo's studio dashboard: Outstanding AR and
// Active Clashes need data Ans does not track yet.
export const ACCOUNT_NAV: AccountNavSection[] = [
  { title: 'Overview', items: [
    { id: 'overview', icon: 'chart', label: 'At a glance', path: '/overview' },
  ] },
  { title: 'Pipeline', items: [
    { id: 'pipeline', icon: 'trophy', label: 'Sales Pipeline', path: '/pipeline' },
    { id: 'clients', icon: 'users', label: 'Clients', path: '/clients' },
    { id: 'invoices', icon: 'creditCard', label: 'Invoices', path: '/invoices' },
    { id: 'client-quotes', icon: 'document', label: 'Client Quotes', path: '/client-quotes' },
  ] },
  { title: 'Delivery', items: [
    { id: 'projects', icon: 'folder', label: 'Projects', path: '/projects' },
    { id: 'vendor-directory', icon: 'briefcase', label: 'Vendor Directory', path: '/vendor-directory' },
    { id: 'account-calendar', icon: 'calendar', label: 'Calendar', path: '/account-calendar', comingSoon: !EVENTS_ENABLED },
  ] },
  // The people list needs an admin. Settings opens to admins too because WhatsApp setup is theirs as well;
  // the page shows an admin only that section and keeps Billing and Vendor categories for owners.
  { title: 'Workspace', items: [
    { id: 'workspace-team', icon: 'user', label: 'Users', path: '/workspace/users', access: 'admin' },
    { id: 'workspace-settings', icon: 'settings', label: 'Settings', path: '/workspace/settings', access: 'admin' },
  ] },
];

export interface AccountNavRole {
  isOwner: boolean;
  isAdmin: boolean;
}

export const canOpenAccountItem = (access: AccountNavAccess | undefined, role: AccountNavRole): boolean =>
  access === 'owner' ? role.isOwner : access === 'admin' ? role.isAdmin : true;

/** The sections this person sees; a section with nothing left in it disappears. */
export function visibleAccountNav(role: AccountNavRole): AccountNavSection[] {
  return ACCOUNT_NAV
    .map(section => ({ ...section, items: section.items.filter(item => canOpenAccountItem(item.access, role)) }))
    .filter(section => section.items.length > 0);
}

export const findAccountNavItem = (id: string): AccountNavItem | undefined =>
  ACCOUNT_NAV.flatMap(s => s.items).find(item => item.id === id);
